/**
 * Header search (issue #234). Markup: snippets/header-search.liquid.
 * Results: sections/predictive-search.liquid via the Section Rendering API.
 *
 * States (data-state): initial (no keyword), results, empty (no matches).
 * - Results for a term are rendered only if that term is still the current one, so a
 *   slow response can never overwrite a newer one.
 * - The previous state stays on screen until the next one is ready; the panel only
 *   dims if a request takes longer than LOADING_DIM_DELAY, so fast typing never flickers.
 * - Below 990px the component is a full-screen dialog opened by the header icon.
 */
class PredictiveSearch extends HTMLElement {
  static DEBOUNCE = 180;
  static LOADING_DIM_DELAY = 250;
  static CLOSE_FADE_FALLBACK = 600;

  connectedCallback() {
    // Re-attached after a theme-editor reload: only the page-level listener needs restoring.
    if (this.form) {
      this.mobileQuery.addEventListener('change', this.onBreakpointChange);
      return;
    }

    // Kept here: parentElement is already null in disconnectedCallback.
    this.wrapper = this.parentElement;
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[type="search"]');
    this.panel = this.querySelector('[data-search-panel]');
    this.resultsSlot = this.querySelector('[data-search-results-slot]');
    this.clearButton = this.querySelector('[data-search-clear]');
    this.initialSlot = this.querySelector('[data-search-initial]');
    this.initialTemplate = this.querySelector('template[data-search-initial-template]');
    this.statusElement = this.querySelector('[data-search-status]');
    this.trigger = document.querySelector(`[aria-controls="${this.id}"]`);
    this.predictiveEnabled = this.dataset.predictive !== 'false';
    this.mobileQuery = window.matchMedia('(max-width: 989px)');

    this.cache = new Map();
    this.term = '';
    this.isOpen = false;
    this.activeIndex = -1;
    this.abortController = null;
    this.debounceTimer = null;
    this.dimTimer = null;
    this.closeTimer = null;

    this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
    this.onBreakpointChange = () => {
      this.close();
      this.updatePlaceholder();
    };

    // Nothing merchandised in the initial state: keep the desktop dropdown shut until typing.
    if (!this.initialTemplate?.content.querySelector('.sc-search__block')) this.setAttribute('data-initial-empty', '');

    const openFromInput = () => {
      this.open();
      // A term already in the field (e.g. on /search?q=…) shows its results.
      if (this.input.value.trim() !== this.term) this.onInput();
    };
    this.input.addEventListener('focus', openFromInput);
    // After Esc the input keeps focus, so a click has to reopen it.
    this.input.addEventListener('click', openFromInput);
    this.input.addEventListener('input', () => this.onInput());
    this.addEventListener('keydown', (event) => this.onKeydown(event));
    this.addEventListener('focusout', (event) => this.onFocusOut(event));
    this.form.addEventListener('submit', (event) => this.onSubmit(event));
    this.clearButton?.addEventListener('click', () => this.clear());
    this.querySelectorAll('[data-search-close]').forEach((button) =>
      button.addEventListener('click', () => this.close({ restoreFocus: true }))
    );
    this.trigger?.addEventListener('click', () => this.open({ focusInput: true }));
    this.mobileQuery.addEventListener('change', this.onBreakpointChange);

    this.updatePlaceholder();
  }

  get isMobile() {
    return this.mobileQuery.matches;
  }

  updatePlaceholder() {
    const placeholder = this.isMobile ? this.dataset.placeholderMobile : this.dataset.placeholderDesktop;
    if (placeholder) this.input.placeholder = placeholder;
  }

  open({ focusInput = false } = {}) {
    if (!this.isOpen) {
      this.stampInitialContent();
      this.isOpen = true;
      this.setAttribute('open', '');
      this.cancelLowerWrapper();
      this.wrapper?.classList.add('is-search-open');
      this.input.setAttribute('aria-expanded', 'true');
      this.trigger?.setAttribute('aria-expanded', 'true');
      document.addEventListener('pointerdown', this.onDocumentPointerDown);
      if (this.isMobile) {
        this.scrollLocked = true;
        document.body.classList.add('overflow-hidden');
        // Full-screen below 990px, so it is a modal dialog there.
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        this.setAttribute('aria-label', this.dataset.dialogLabel || '');
      }
    }
    // Focus inside the tap handler so iOS raises the keyboard. The sheet's
    // visibility flips to visible with no delay, so it is focusable right away.
    if (focusInput && document.activeElement !== this.input) this.input.focus({ preventScroll: true });
  }

  close({ restoreFocus = false } = {}) {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.removeAttribute('open');
    this.lowerWrapperAfterFade();
    this.input.setAttribute('aria-expanded', 'false');
    this.trigger?.setAttribute('aria-expanded', 'false');
    this.setActive(-1);
    document.removeEventListener('pointerdown', this.onDocumentPointerDown);
    this.releasePage();

    if (!restoreFocus) return;
    if (this.isMobile && this.trigger) {
      this.trigger.focus({ preventScroll: true });
    } else if (this.contains(document.activeElement) && document.activeElement !== this.input) {
      this.input.focus({ preventScroll: true });
      // Focusing the input would reopen the panel; keep it closed.
      this.close();
    }
  }

  // The wrapper's raised z-index has to outlast the sheet's fade, or the tablet header
  // icons pop over it while it fades out. The sheet's visibility transition ends with
  // the fade; the timeout covers breakpoints and reduced motion, where nothing transitions.
  lowerWrapperAfterFade() {
    this.cancelLowerWrapper();
    // The close fade runs only while data-closing is set (see header-search.liquid).
    this.setAttribute('data-closing', '');
    const lower = () => {
      this.cancelLowerWrapper();
      this.wrapper?.classList.remove('is-search-open');
    };
    this.onCloseTransitionEnd = (event) => {
      if (event.target === this && event.propertyName === 'visibility') lower();
    };
    this.addEventListener('transitionend', this.onCloseTransitionEnd);
    this.closeTimer = setTimeout(lower, PredictiveSearch.CLOSE_FADE_FALLBACK);
  }

  cancelLowerWrapper() {
    clearTimeout(this.closeTimer);
    this.closeTimer = null;
    if (this.onCloseTransitionEnd) this.removeEventListener('transitionend', this.onCloseTransitionEnd);
    this.onCloseTransitionEnd = null;
    this.removeAttribute('data-closing');
  }

  disconnectedCallback() {
    // Theme-editor section reloads remove the element; leave nothing running or locked.
    this.mobileQuery.removeEventListener('change', this.onBreakpointChange);
    document.removeEventListener('pointerdown', this.onDocumentPointerDown);
    clearTimeout(this.debounceTimer);
    this.cancelLowerWrapper();
    this.abort();
    this.isOpen = false;
    this.removeAttribute('open');
    this.wrapper?.classList.remove('is-search-open');
    this.releasePage();
  }

  stampInitialContent() {
    if (!this.initialTemplate || !this.initialSlot) return;
    this.initialSlot.appendChild(this.initialTemplate.content.cloneNode(true));
    this.initialTemplate.remove();
    this.initialTemplate = null;
  }

  releasePage() {
    if (this.scrollLocked) {
      this.scrollLocked = false;
      document.body.classList.remove('overflow-hidden');
    }
    this.removeAttribute('role');
    this.removeAttribute('aria-modal');
    this.removeAttribute('aria-label');
  }

  onDocumentPointerDown(event) {
    if (this.isMobile) return;
    if (!this.contains(event.target)) this.close();
  }

  onFocusOut(event) {
    if (this.isMobile) return;
    // Close only when focus moves to something else on the page (e.g. Tab out). A click
    // on the panel's padding, or Safari not focusing clicked links, leaves relatedTarget
    // null; outside clicks are handled by onDocumentPointerDown.
    if (event.relatedTarget && !this.contains(event.relatedTarget)) this.close();
  }

  clear() {
    this.input.value = '';
    this.onInput();
    this.input.focus({ preventScroll: true });
  }

  onInput() {
    const term = this.input.value.trim();
    if (this.clearButton) this.clearButton.hidden = this.input.value.length === 0;
    if (term === this.term) return;
    this.term = term;
    this.setActive(-1);
    clearTimeout(this.debounceTimer);

    if (!term) {
      this.abort();
      this.setState('initial');
      return;
    }

    this.open();
    if (!this.predictiveEnabled) return;

    const cached = this.cache.get(term.toLowerCase());
    if (cached) {
      this.abort();
      this.render(cached);
      return;
    }
    this.debounceTimer = setTimeout(() => this.fetchResults(term), PredictiveSearch.DEBOUNCE);
  }

  abort() {
    this.abortRequest();
    this.setLoading(false);
  }

  abortRequest() {
    this.abortController?.abort();
    this.abortController = null;
  }

  fetchResults(term) {
    // Replace the request but keep the loading state: resetting it here would undim
    // and re-dim the panel on every keystroke of a slow search.
    this.abortRequest();
    this.abortController = new AbortController();
    this.setLoading(true);

    const params = new URLSearchParams({
      q: term,
      section_id: 'predictive-search',
      'resources[type]': 'query,collection,product',
      'resources[limit]': '6',
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last',
    });

    fetch(`${window.routes.predictive_search_url}?${params}`, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Predictive search ${response.status}`);
        return response.text();
      })
      .then((text) => {
        const section = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');
        if (!section) throw new Error('Predictive search section missing');
        const markup = section.innerHTML;
        this.cache.set(term.toLowerCase(), markup);
        if (term !== this.term) return;
        this.setLoading(false);
        this.render(markup);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        if (term !== this.term) return;
        // Don't leave the previous term's results up as if they matched this one.
        this.resultsSlot.innerHTML = '';
        this.setState('initial');
        if (this.statusElement) this.statusElement.textContent = '';
        // Enter still submits to the full search page.
        this.setLoading(false);
      });
  }

  setLoading(isLoading) {
    if (isLoading) {
      this.setAttribute('aria-busy', 'true');
      // Once dimmed, stay dimmed until something renders; a running timer keeps running.
      if (!this.hasAttribute('data-loading-slow') && !this.dimTimer) {
        this.dimTimer = setTimeout(() => {
          this.dimTimer = null;
          this.setAttribute('data-loading-slow', '');
        }, PredictiveSearch.LOADING_DIM_DELAY);
      }
    } else {
      clearTimeout(this.dimTimer);
      this.dimTimer = null;
      this.removeAttribute('aria-busy');
      this.removeAttribute('data-loading-slow');
    }
  }

  render(markup) {
    this.resultsSlot.innerHTML = markup;
    const body = this.resultsSlot.querySelector('[data-search-results]');
    const total = Number(body?.dataset.total || 0);
    this.setState(total > 0 ? 'results' : 'empty');
    this.panel.scrollTop = 0;

    const liveText = this.resultsSlot.querySelector('[data-search-live-region]')?.textContent.trim();
    if (liveText && this.statusElement) this.statusElement.textContent = liveText;
  }

  setState(state) {
    this.dataset.state = state;
  }

  visibleOptions() {
    return Array.from(this.panel.querySelectorAll('[data-search-option]')).filter(
      (option) => option.offsetParent !== null
    );
  }

  setActive(index) {
    const options = this.visibleOptions();
    const previous = this.panel.querySelector('[aria-selected="true"]');
    previous?.removeAttribute('aria-selected');
    this.activeIndex = index;

    const option = options[index];
    if (!option) {
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
      return;
    }
    if (!option.id) option.id = `${this.id}-option-${index}-${Date.now()}`;
    option.setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', option.id);
    option.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.isOpen) {
      event.preventDefault();
      this.close({ restoreFocus: true });
      return;
    }

    if (event.target === this.input && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      const options = this.visibleOptions();
      if (!options.length) return;
      event.preventDefault();
      this.open();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      const next = this.activeIndex === -1 && step === -1 ? options.length - 1 : this.activeIndex + step;
      this.setActive(next < 0 || next >= options.length ? -1 : next);
      return;
    }

    if (event.key === 'Tab' && this.isOpen && this.isMobile) this.trapFocus(event);
  }

  trapFocus(event) {
    const focusable = Array.from(this.querySelectorAll('input:not([type="hidden"]), button, a[href]')).filter(
      (element) => element.offsetParent !== null && !element.hidden
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  onSubmit(event) {
    const active = this.visibleOptions()[this.activeIndex];
    if (active) {
      event.preventDefault();
      active.click();
      return;
    }
    if (!this.input.value.trim()) event.preventDefault();
  }
}

if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
